import { useCallback } from 'react';
import { Button, IconPlus, Stack, Text } from '@adc/parallax-component-library';
import { createFileRoute, Outlet, useNavigate } from '@tanstack/react-router';

import type { IDN, IDNSort } from '~/api/types';
import { IDNTable } from '~/components/table/idn/IDNTable';
import type { IDNTableColumnKey } from '~/components/table/idn/types';
import { USER_ROLES } from '~/features/auth/roles';
import { useModal } from '~/features/modals/useModal';
import { Route as IDNEditRoute } from '~/routes/customer-admin/idns/$IDNId';
import type { IDNSearchParams } from '~/types/IDNRoute';
import { buildTestId } from '~/utils/buildTestId';
import { protectedRoute } from '~/utils/routesAuthorization';

const TEST_ID = 'IDN-table';

export const Route = createFileRoute('/customer-admin/idns')({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>): IDNSearchParams => {
    // Validate and parse the search params into a typed state
    const defaultSortDir = search.sortDir === 'asc' ? 'asc' : 'desc';
    const sortDir = search.sortDir ? defaultSortDir : undefined;

    return {
      page: search.page ? Number(search.page) || 1 : undefined,
      sort: (search.sort as IDNSort | undefined) ?? undefined,
      search: search.search ? decodeURI(search.search as string) : undefined,
      sortDir,
    };
  },
  beforeLoad: () => {
    protectedRoute({
      roles: [USER_ROLES.AdminAbbott, USER_ROLES.AbbottUser],
    });
  },
});

function RouteComponent() {
  const {
    page = 1,
    search = '',
    sort = '',
    sortDir = 'desc',
  } = Route.useSearch();

  const navigate = useNavigate({ from: Route.fullPath });

  const { openModal } = useModal();

  const handleSearch = async (searchValue: string) => {
    // Update the search term and reset to the first page
    await navigate({
      search: (prev) => ({ ...prev, page: 1, search: encodeURI(searchValue) }),
    });
  };

  const handlePageChange = async (newPage: number) => {
    // Update the page number in the search params
    await navigate({
      search: (prev) => ({ ...prev, page: newPage }),
    });
  };

  const handleSort = async (columnKey: IDNTableColumnKey) => {
    // Update the sort column and direction in the search params
    await navigate({
      search: (prev) => ({
        ...prev,
        sort: columnKey,
        sortDir: sort === columnKey && sortDir === 'asc' ? 'desc' : 'asc',
      }),
    });
  };

  const onRowPress = useCallback(
    async (idn: IDN) => {
      await navigate({
        to: IDNEditRoute.fullPath,
        params: {
          IDNId: idn.IDNId,
        },
        search: (prev) => ({
          ...prev,
        }),
      });
    },
    [navigate],
  );

  return (
    <Stack height="auto" testID={TEST_ID}>
      <Text
        type="$h3.medium"
        marginBottom="$2"
        testID={buildTestId({ testID: TEST_ID, suffix: 'title' })}
      >
        IDN Administration
      </Text>
      <IDNTable
        testID={TEST_ID}
        page={page}
        search={search}
        sort={sort}
        sortDir={sortDir}
        onPageChange={handlePageChange}
        onSearch={handleSearch}
        onSortChange={handleSort}
        onRowPress={onRowPress}
        headerActions={
          <Button
            testID={buildTestId({
              testID: TEST_ID,
              suffix: ['add', 'department', 'button'],
            })}
            size="medium"
            variant="primary"
            label="Add IDN"
            iconStart={<IconPlus />}
            onPress={() => {
              openModal('IDNForm', { testID: 'idn-create-modal' });
            }}
          />
        }
      />
      <Outlet />
    </Stack>
  );
}
